/**
 * Subdomain-based routing utilities for Booking System
 *
 * URL Format: {company}.{base-domain}/{layout-slug}
 *
 * Examples:
 * - Local:      acme.localhost:8080/my-directory
 * - Staging:    acme.staging.slashexperts.com/my-directory
 * - Production: acme.slashexperts.com/my-directory
 * - Default:    acme.slashexperts.com (uses first template)
 *
 * Returns null when:
 * - localhost:8080 (no subdomain)
 * - test-manage.slashexperts.com (reserved for main app)
 * - manage.slashexperts.com (reserved for main app)
 */

// Base domains for booking system subdomain routing
// Order matters: more specific domains should come first
const BASE_DOMAINS = [
  "localhost",
  "staging.slashexperts.com", // Staging
  "slashexperts.com", // Production
];

// Reserved subdomains that should NOT be treated as company names
// These are used by the main application and infrastructure
// Keep in sync with Company::RESERVED_SLUGS in the backend
const RESERVED_SUBDOMAINS = new Set([
  "manage", // Production main app
  "test-manage", // Staging main app
  "api",
  "app",
  "test-app",
  "staging-api",
  "staging",
  "staging-manage",
  "carly", // Carly / Manager production host
]);

/**
 * Extracts the company/program slug from subdomain
 * e.g., acme.slashexperts.com → "acme"
 * e.g., acme.staging.slashexperts.com → "acme"
 *
 * Returns null for reserved subdomains (manage, test-manage, etc.)
 */
export function getCompanyFromSubdomain(): string | null {
  const hostname = window.location.hostname;

  // Check each base domain
  for (const baseDomain of BASE_DOMAINS) {
    // If hostname ends with .baseDomain, extract the subdomain
    if (hostname.endsWith(`.${baseDomain}`)) {
      const subdomain = hostname.slice(
        0,
        hostname.length - baseDomain.length - 1
      );
      // Only return if it's a single-level subdomain (no dots)
      // and not a reserved subdomain
      if (
        subdomain &&
        !subdomain.includes(".") &&
        !RESERVED_SUBDOMAINS.has(subdomain)
      ) {
        return subdomain;
      }
    }
  }

  return null;
}

/**
 * Gets the layout slug from the URL path
 * e.g., /my-directory → "my-directory"
 * e.g., / → null (default template)
 */
export function getLayoutSlugFromPath(): string | null {
  const pathname = window.location.pathname;
  // Remove leading slash and get the first path segment
  const slug = pathname.replace(/^\//, "").split("/")[0];
  return slug || null;
}

/**
 * @deprecated Use getLayoutSlugFromPath instead
 * Gets the layout slug from the 'layout' query parameter
 * e.g., ?layout=my-directory → "my-directory"
 */
export function getLayoutSlugFromQuery(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("layout");
}

/**
 * Check if current URL is a subdomain-based booking system route
 * Requires: subdomain (company)
 * Optional: ?layout= query param (layout slug) - defaults to first template if not provided
 */
export function isBookingSystemSubdomainRoute(): boolean {
  return getCompanyFromSubdomain() !== null;
}

/**
 * Check if we're on any subdomain (even without query params)
 */
export function isSubdomainRoute(): boolean {
  return getCompanyFromSubdomain() !== null;
}

export function getBaseDomain(): string {
  const hostname = window.location.hostname;

  for (const baseDomain of BASE_DOMAINS) {
    if (hostname === baseDomain || hostname.endsWith(`.${baseDomain}`)) {
      return baseDomain;
    }
  }

  return hostname;
}

/**
 * Generate booking system URL based on environment:
 * - Local:      http://{companySlug}.localhost:8080/{layoutSlug}
 * - Staging:    https://{companySlug}.staging.slashexperts.com/{layoutSlug}
 * - Production: https://{companySlug}.slashexperts.com/{layoutSlug}
 */
export function getBookingSystemUrl(
  companySlug: string,
  layoutSlug: string
): string {
  const env = (
    process.env.VUE_APP_ENV ||
    process.env.NODE_ENV ||
    "development"
  ).toLowerCase();

  if (env === "production") {
    return `https://${companySlug}.slashexperts.com/${layoutSlug}`;
  } else if (env === "staging") {
    return `https://${companySlug}.staging.slashexperts.com/${layoutSlug}`;
  } else {
    // Local/development
    return `http://${companySlug}.localhost:8080/${layoutSlug}`;
  }
}
